import { useState, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, User } from 'lucide-react';
import { useShopifyCartStore } from '@/stores/shopifyCartStore';
import { useAuth } from '@/hooks/useAuth';

const logo = '/images/logo-sm.webp';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'About', path: '/about' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Shop', path: '/shop' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
  { label: 'Booking', path: '/booking' },
  { label: 'Galleries', path: '/galleries' },
];

export const Header = forwardRef<HTMLElement>(function Header(_, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const totalItems = useShopifyCartStore(state => state.totalItems());
  const { user, isAdmin } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-primary/10 bg-background/70 backdrop-blur-2xl">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:text-sm">
        Skip to main content
      </a>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Raven Moon Photography — Home" className="h-14 w-auto" width="56" height="56" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-cinzel text-xs tracking-[0.18em] uppercase transition-all duration-500 hover:text-primary relative group ${
                  isActive(item.path) ? 'text-primary' : 'text-foreground/82'
                }`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-primary transition-all duration-500 ${
                  isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}



            <Link to="/booking">
              <span className="inline-flex items-center justify-center rounded-full border border-primary/25 bg-gradient-gold px-6 py-3 font-cinzel text-[11px] tracking-[0.24em] uppercase text-primary-foreground shadow-ethereal transition-all duration-500 glow-hover-subtle">
                Book Now
              </span>
            </Link>

            <Link
              to="/cart"
              aria-label="Shopping cart"
              className="relative rounded-full p-2 text-foreground/80 hover:bg-card/60 hover:text-primary transition-all duration-500"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </Link>

            {user && isAdmin && (
              <Link
                to="/admin"
                aria-label="Admin dashboard"
                className="rounded-full p-2 text-primary/80 hover:bg-card/60 hover:text-primary transition-all duration-500"
                title="Admin Dashboard"
              >
                <User className="h-5 w-5" />
              </Link>
            )}
          </nav>

          {/* Mobile */}
          <div className="flex items-center gap-4 lg:hidden">
            <Link
              to="/cart"
              aria-label="Shopping cart"
              className="relative rounded-full p-2 text-foreground/80 hover:bg-card/60 hover:text-primary transition-all duration-500"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </Link>
            {user && isAdmin && (
              <Link to="/admin" aria-label="Admin dashboard" className="rounded-full p-2 text-primary/80 hover:bg-card/60 hover:text-primary transition-all duration-500">
                <User className="h-5 w-5" />
              </Link>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="rounded-full p-2 text-foreground/90 transition-colors hover:bg-card/60 hover:text-primary"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden animate-fade-in pb-4">
            <nav className="ethereal-panel mt-3 px-5 py-4" aria-label="Mobile navigation">
              <div className="mb-3 flex items-center justify-center">
                <span className="eyebrow-label text-center">Navigate the Collection</span>
              </div>

              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`group flex items-center justify-between border-b border-border/35 py-4 font-cinzel text-[15px] tracking-[0.18em] uppercase transition-all duration-500 ${
                    isActive(item.path) ? 'text-primary' : 'text-foreground/92 hover:text-primary'
                  }`}
                >
                  <span>{item.label}</span>
                  <span className={`h-px w-8 bg-primary/35 transition-all duration-500 ${isActive(item.path) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
                </Link>
              ))}

              <Link
                to="/booking"
                onClick={() => setIsOpen(false)}
                className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-gradient-gold px-6 py-4 font-cinzel text-[12px] tracking-[0.24em] uppercase text-primary-foreground shadow-ethereal transition-all duration-500 glow-hover"
              >
                Book Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
});

Header.displayName = 'Header';